import { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  MenuItem,
  Paper,
  Select,
  Slider,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import DevicesRoundedIcon from "@mui/icons-material/DevicesRounded";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import LocalHospitalRoundedIcon from "@mui/icons-material/LocalHospitalRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import logo from "../assets/saccascan-logo.png";
import {
  AwvBand,
  AwvInputMode,
  CalculatorInputs,
  PracticeType,
  SupportLevel,
  calculatePlan,
} from "../features/pricing/calculator";
import {
  ProfessionalMatch,
  isValidProfessionalName,
  isValidUsZip,
  verifyProfessional,
} from "../features/verification/npiVerification";

const TEAL = "#087d7f";
const TEAL_DARK = "#075a61";
const INK = "#102a35";
const MUTED = "#5b6f78";
const BORDER = "#dce7e8";
const NPI_DOCUMENTATION_URL = "https://clinicaltables.nlm.nih.gov/apidoc/npi_idv/v3/doc.html";

type VerificationStatus = "idle" | "checking" | "confirmed" | "not_confirmed" | "error";

const money = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

const moneyWithCents = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const practiceOptions: Array<{
  value: PracticeType;
  label: string;
  detail: string;
  icon: typeof LocalHospitalRoundedIcon;
}> = [
  { value: "individual", label: "Individual PCP", detail: "One clinician", icon: LocalHospitalRoundedIcon },
  { value: "small", label: "Small practice", detail: "2–4 PCPs", icon: DevicesRoundedIcon },
  { value: "organization", label: "Organization", detail: "5+ PCPs", icon: BusinessRoundedIcon },
];

const awvOptions: Array<{ value: AwvBand; label: string }> = [
  { value: "under_200", label: "<200" },
  { value: "200_499", label: "200–499" },
  { value: "500_999", label: "500–999" },
  { value: "1000_1999", label: "1,000–1,999" },
  { value: "2000_plus", label: "2,000+" },
];

const supportOptions: Array<{ value: SupportLevel; label: string; detail: string }> = [
  { value: "core", label: "Core", detail: "Standard administration, reporting and support" },
  { value: "advanced", label: "Advanced", detail: "Centralized administration, analytics, SSO and enhanced support" },
  { value: "system", label: "System", detail: "Advanced security, reporting and formal SLA requirements" },
  { value: "health_system", label: "Health System", detail: "Complex IT, governance and implementation requirements" },
  { value: "unsure", label: "I’m not sure", detail: "We’ll suggest the likely level from your organization size" },
];

const ehrOptions = [
  "Epic",
  "Oracle Health / Cerner",
  "athenahealth",
  "eClinicalWorks",
  "NextGen",
  "Other",
  "Not sure",
];

const sectionSx = {
  border: `1px solid ${BORDER}`,
  borderRadius: 3,
  p: { xs: 2, sm: 3 },
  bgcolor: "#fff",
};

function SectionHeader({ number, title, description }: { number: string; title: string; description: string }) {
  return (
    <Stack direction="row" spacing={1.5} sx={{ alignItems: "flex-start", mb: 2.5 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          bgcolor: "#e8f5f3",
          color: TEAL_DARK,
          display: "grid",
          placeItems: "center",
          fontWeight: 800,
          flex: "0 0 auto",
        }}
      >
        {number}
      </Box>
      <Box>
        <Typography variant="h6" sx={{ color: INK, fontWeight: 800, lineHeight: 1.2 }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: MUTED, mt: 0.5 }}>
          {description}
        </Typography>
      </Box>
    </Stack>
  );
}

function CalculatorPage() {
  const [verificationName, setVerificationName] = useState("");
  const [verificationZip, setVerificationZip] = useState("");
  const [verificationStatus, setVerificationStatus] = useState<VerificationStatus>("idle");
  const [professionalMatch, setProfessionalMatch] = useState<ProfessionalMatch | null>(null);
  const [subscriptionEmail, setSubscriptionEmail] = useState("");
  const [subscriptionEmailTouched, setSubscriptionEmailTouched] = useState(false);
  const [inputs, setInputs] = useState<CalculatorInputs>({
    practiceType: "individual",
    pcpCount: 1,
    awvBand: "under_200",
    awvInputMode: "practice_total",
    supportLevel: "unsure",
    ehr: "",
    includeStandardEhrIntegration: false,
  });

  useEffect(() => {
    const previousTitle = document.title;
    document.title = "MultiCog Plan Calculator | SaccaScan";

    const definitions = [
      { selector: 'meta[name="description"]', attribute: "name", key: "description", content: "Find the right MultiCog plan and calculate monthly, annual and per-PCP subscription pricing." },
      { selector: 'meta[property="og:title"]', attribute: "property", key: "og:title", content: "MultiCog Plan Calculator" },
      { selector: 'meta[property="og:description"]', attribute: "property", key: "og:description", content: "Find the right plan for your practice with transparent monthly, annual and per-PCP pricing." },
      { selector: 'meta[property="og:type"]', attribute: "property", key: "og:type", content: "website" },
      { selector: 'meta[property="og:url"]', attribute: "property", key: "og:url", content: "https://saccadata.cloud/calculator" },
      { selector: 'meta[property="og:image"]', attribute: "property", key: "og:image", content: "https://saccadata.cloud/calculator-og.png" },
      { selector: 'meta[name="twitter:card"]', attribute: "name", key: "twitter:card", content: "summary_large_image" },
    ] as const;
    const changes = definitions.map((definition) => {
      let element = document.head.querySelector<HTMLMetaElement>(definition.selector);
      const created = !element;
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(definition.attribute, definition.key);
        document.head.appendChild(element);
      }
      const previousContent = element.content;
      element.content = definition.content;
      return { element, created, previousContent };
    });

    return () => {
      document.title = previousTitle;
      changes.forEach(({ element, created, previousContent }) => {
        if (created) element.remove();
        else element.content = previousContent;
      });
    };
  }, []);

  const result = useMemo(() => calculatePlan(inputs), [inputs]);
  const displayedPcpCount = inputs.practiceType === "individual" ? 1 : inputs.pcpCount;
  const isStandardSelection = inputs.practiceType === "individual" && inputs.awvBand === "under_200";
  const isSubscriptionEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(subscriptionEmail.trim());

  const update = <K extends keyof CalculatorInputs>(key: K, value: CalculatorInputs[K]) => {
    setInputs((current) => ({ ...current, [key]: value }));
  };

  const handlePracticeChange = (_: React.MouseEvent<HTMLElement>, next: PracticeType | null) => {
    if (!next) return;
    const pcpCount = next === "individual" ? 1 : next === "small" ? 2 : 5;
    setInputs((current) => ({ ...current, practiceType: next, pcpCount }));
  };

  const resetVerification = () => {
    setVerificationStatus("idle");
    setProfessionalMatch(null);
  };

  const handleVerification = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!isValidProfessionalName(verificationName) || !isValidUsZip(verificationZip)) {
      setProfessionalMatch(null);
      setVerificationStatus("not_confirmed");
      return;
    }

    setVerificationStatus("checking");
    setProfessionalMatch(null);
    try {
      const verification = await verifyProfessional(verificationName, verificationZip);
      if (verification.status === "confirmed") {
        setProfessionalMatch(verification.match);
        setVerificationStatus("confirmed");
      } else {
        setVerificationStatus("not_confirmed");
      }
    } catch {
      setVerificationStatus("error");
    }
  };

  const subscriptionMailto = (intent: "now" | "later") => {
    const intentLabel = intent === "now" ? "Subscribe now" : "Subscribe later";
    const price = result.requiresQuote
      ? "Custom quote"
      : `${money.format(result.monthlyTotal ?? 0)}/month (${money.format(result.annualTotal ?? 0)}/year)`;
    const message = [
      `Subscription intent: ${intentLabel}`,
      `Email: ${subscriptionEmail.trim()}`,
      `Verified professional: ${professionalMatch?.name ?? verificationName}`,
      professionalMatch ? `NPI: ${professionalMatch.npi}` : "",
      `Recommended plan: ${result.planName}`,
      `Price: ${price}`,
      `Participating PCPs: ${displayedPcpCount}`,
      `Annual AWV input: ${inputs.practiceType === "individual" ? "per PCP" : inputs.awvInputMode === "practice_total" ? "practice/organization total" : "typical range per PCP"}`,
      `Annual AWV band: ${inputs.awvBand}`,
      `EHR: ${inputs.ehr || "Not specified"}`,
    ].filter(Boolean).join("\n");
    return `mailto:peterernster@icloud.com?subject=${encodeURIComponent(`MultiCog — ${intentLabel}`)}&body=${encodeURIComponent(message)}`;
  };

  const handleSubscriptionClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    setSubscriptionEmailTouched(true);
    if (!isSubscriptionEmailValid) event.preventDefault();
  };

  const resetCalculator = () => {
    setInputs({
      practiceType: "individual",
      pcpCount: 1,
      awvBand: "under_200",
      awvInputMode: "practice_total",
      supportLevel: "unsure",
      ehr: "",
      includeStandardEhrIntegration: false,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f5f9f8", color: INK }}>
      <Box component="header" sx={{ bgcolor: "rgba(255,255,255,0.96)", borderBottom: `1px solid ${BORDER}` }}>
        <Container maxWidth="lg">
          <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", minHeight: 76, py: 1 }}>
            <Box component="img" src={logo} alt="SaccaScan.ai" sx={{ width: { xs: 190, sm: 224 }, height: "auto" }} />
            <Chip
              label="Plan calculator"
              sx={{ display: { xs: "none", sm: "inline-flex" }, bgcolor: "#e8f5f3", color: TEAL_DARK, fontWeight: 700 }}
            />
          </Stack>
        </Container>
      </Box>

      <Box
        component="section"
        sx={{
          background: "linear-gradient(145deg, #082f36 0%, #075f65 58%, #0b8580 100%)",
          color: "white",
          py: { xs: 6, md: 8 },
          position: "relative",
          overflow: "hidden",
          "&::after": {
            content: '""',
            position: "absolute",
            width: 420,
            height: 420,
            borderRadius: "50%",
            border: "80px solid rgba(255,255,255,0.055)",
            right: -120,
            top: -180,
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Typography sx={{ color: "#9ee3dc", fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", fontSize: 13 }}>
            MultiCog subscription planning
          </Typography>
          <Typography component="h1" sx={{ fontSize: { xs: 38, sm: 52, md: 62 }, lineHeight: 1.02, fontWeight: 850, maxWidth: 760, mt: 1.5, letterSpacing: "-0.035em" }}>
            Find the right plan for your practice.
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.78)", fontSize: { xs: 17, sm: 20 }, maxWidth: 690, mt: 2.5, lineHeight: 1.55 }}>
            Adjust your practice size, assessment volume and support needs. Your recommendation and price update instantly.
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 1, sm: 3 }} sx={{ mt: 3.5, color: "rgba(255,255,255,0.88)" }}>
            {["Transparent monthly price", "No patient information", "About one minute"].map((item) => (
              <Stack direction="row" spacing={0.8} sx={{ alignItems: "center" }} key={item}>
                <CheckRoundedIcon sx={{ fontSize: 19, color: "#9ee3dc" }} />
                <Typography variant="body2" sx={{ fontWeight: 650 }}>{item}</Typography>
              </Stack>
            ))}
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 7 } }}>
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            p: { xs: 2.5, sm: 3.5 },
            borderRadius: 3,
            border: `1px solid ${BORDER}`,
            borderLeft: `5px solid ${TEAL}`,
            bgcolor: "#eef8f6",
          }}
        >
          <Typography component="h2" sx={{ color: INK, fontSize: { xs: 23, sm: 28 }, fontWeight: 900, letterSpacing: "-0.02em" }}>
            No nonsense. No waiting. No spam.
          </Typography>
          <Typography sx={{ color: MUTED, mt: 1.25, maxWidth: 980, lineHeight: 1.65 }}>
            We collect only the information needed to verify your identity and professional status. Once you complete the brief registration form, your details are checked against publicly available healthcare professional records. Once verification is confirmed, you receive immediate access to the calculator.
          </Typography>
          <Typography sx={{ color: MUTED, mt: 1, maxWidth: 980, lineHeight: 1.65 }}>
            We will not add you to a marketing list or flood your inbox with follow-up messages. Registration is required because certain pricing, reimbursement and implementation information on this website is intended only for verified healthcare professionals.
          </Typography>
        </Paper>

        <Paper
          component="section"
          elevation={0}
          sx={{ ...sectionSx, mb: 3 }}
          aria-labelledby="verification-heading"
        >
          <Stack direction={{ xs: "column", md: "row" }} spacing={3} sx={{ justifyContent: "space-between", alignItems: { md: "flex-start" } }}>
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" spacing={1.25} sx={{ alignItems: "center", mb: 0.75 }}>
                <ShieldOutlinedIcon sx={{ color: TEAL, fontSize: 29 }} />
                <Typography id="verification-heading" component="h2" sx={{ color: INK, fontSize: 24, fontWeight: 900 }}>
                  Verification
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ color: MUTED, lineHeight: 1.6, maxWidth: 610 }}>
                Enter your full professional name and the ZIP code associated with your practice. The lookup runs directly against U.S. National Library of Medicine individual NPI records derived from CMS data; SaccaScan does not store these verification details.
              </Typography>
              <Typography
                component="a"
                href={NPI_DOCUMENTATION_URL}
                target="_blank"
                rel="noreferrer"
                variant="caption"
                sx={{ color: TEAL_DARK, display: "inline-block", mt: 0.75, fontWeight: 700 }}
              >
                About the public NPI data source ↗
              </Typography>
            </Box>

            <Box component="form" onSubmit={handleVerification} sx={{ flex: 1, width: "100%", maxWidth: { md: 520 } }}>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1.6fr) minmax(140px, 0.7fr)" }, gap: 1.5 }}>
                <TextField
                  label="Your name"
                  value={verificationName}
                  onChange={(event) => {
                    setVerificationName(event.target.value);
                    resetVerification();
                  }}
                  autoComplete="name"
                  required
                  fullWidth
                  slotProps={{ htmlInput: { "aria-label": "Your name" } }}
                />
                <TextField
                  label="Your ZIP code"
                  value={verificationZip}
                  onChange={(event) => {
                    setVerificationZip(event.target.value);
                    resetVerification();
                  }}
                  autoComplete="postal-code"
                  required
                  fullWidth
                  slotProps={{ htmlInput: { inputMode: "numeric", maxLength: 10, "aria-label": "Your ZIP code" } }}
                />
              </Box>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={verificationStatus === "checking"}
                sx={{ mt: 1.5, py: 1.25, bgcolor: TEAL, textTransform: "none", fontWeight: 850, borderRadius: 2, "&:hover": { bgcolor: TEAL_DARK } }}
              >
                {verificationStatus === "checking" ? <CircularProgress size={23} sx={{ color: "white" }} /> : "Verify professional status"}
              </Button>

              {verificationStatus === "confirmed" && professionalMatch && (
                <Box role="status" sx={{ mt: 1.5, p: 2, borderRadius: 2.5, border: "1px solid #8bc9ad", bgcolor: "#edf9f2", color: "#185d3d" }}>
                  <Stack direction="row" spacing={1.25} sx={{ alignItems: "flex-start" }}>
                    <CheckCircleRoundedIcon sx={{ mt: 0.1 }} />
                    <Box>
                      <Typography sx={{ fontWeight: 900 }}>Verification confirmed</Typography>
                      <Typography variant="body2" sx={{ mt: 0.2 }}>Please, proceed to the calculator.</Typography>
                      <Typography variant="caption" sx={{ display: "block", mt: 0.7, opacity: 0.78 }}>
                        {professionalMatch.name} · {professionalMatch.providerType} · NPI {professionalMatch.npi}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              )}

              {verificationStatus === "not_confirmed" && (
                <Box role="alert" sx={{ mt: 1.5, p: 2, borderRadius: 2.5, border: "1px solid #e2a3a0", bgcolor: "#fff2f1", color: "#8b2925" }}>
                  <Stack direction="row" spacing={1.25} sx={{ alignItems: "flex-start" }}>
                    <CancelRoundedIcon sx={{ mt: 0.1 }} />
                    <Box>
                      <Typography sx={{ fontWeight: 900 }}>Verification not confirmed</Typography>
                      <Typography variant="body2" sx={{ mt: 0.2 }}>Sorry, check your name spelling and/or ZIP.</Typography>
                    </Box>
                  </Stack>
                </Box>
              )}

              {verificationStatus === "error" && (
                <Alert severity="warning" sx={{ mt: 1.5 }}>
                  Verification is temporarily unavailable. Please try again in a moment.
                </Alert>
              )}
            </Box>
          </Stack>
        </Paper>

        {verificationStatus === "confirmed" ? (
          <>
            <Box id="calculator" sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.3fr) minmax(350px, 0.7fr)" }, gap: { xs: 3, md: 4 }, alignItems: "start" }}>
          <Stack spacing={2.5}>
            <Paper elevation={0} sx={sectionSx}>
              <SectionHeader number="1" title="Tell us about your practice" description="This determines the most suitable MultiCog plan." />
              <ToggleButtonGroup
                exclusive
                fullWidth
                value={inputs.practiceType}
                onChange={handlePracticeChange}
                aria-label="Practice type"
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                  gap: 1.25,
                  "& .MuiToggleButton-root": {
                    border: `1px solid ${BORDER} !important`,
                    borderRadius: "12px !important",
                    p: 2,
                    textTransform: "none",
                    justifyContent: "flex-start",
                    alignItems: "flex-start",
                    color: INK,
                  },
                  "& .Mui-selected": { bgcolor: "#e8f5f3 !important", borderColor: `${TEAL} !important`, color: `${TEAL_DARK} !important` },
                }}
              >
                {practiceOptions.map((option) => {
                  const Icon = option.icon;
                  return (
                    <ToggleButton value={option.value} key={option.value}>
                      <Stack direction="row" spacing={1.2} sx={{ textAlign: "left" }}>
                        <Icon sx={{ fontSize: 23, mt: 0.15 }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 800 }}>{option.label}</Typography>
                          <Typography variant="caption" sx={{ color: "inherit", opacity: 0.72 }}>{option.detail}</Typography>
                        </Box>
                      </Stack>
                    </ToggleButton>
                  );
                })}
              </ToggleButtonGroup>

              {inputs.practiceType !== "individual" && (
                <Box sx={{ mt: 3.5 }}>
                  <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                    <Box>
                      <Typography sx={{ fontWeight: 750 }}>Participating PCPs</Typography>
                      <Typography variant="caption" sx={{ color: MUTED }}>Participating physicians licensed to use MultiCog</Typography>
                    </Box>
                    <TextField
                      value={inputs.pcpCount}
                      onChange={(event) => update("pcpCount", Number(event.target.value))}
                      type="number"
                      size="small"
                      slotProps={{ htmlInput: { min: inputs.practiceType === "small" ? 2 : 5, max: inputs.practiceType === "small" ? 4 : 500, "aria-label": "Participating PCP count" } }}
                      sx={{ width: 92, "& input": { textAlign: "center", fontWeight: 800 } }}
                    />
                  </Stack>
                  <Slider
                    value={Math.min(inputs.pcpCount, inputs.practiceType === "small" ? 4 : 100)}
                    min={inputs.practiceType === "small" ? 2 : 5}
                    max={inputs.practiceType === "small" ? 4 : 100}
                    step={1}
                    onChange={(_, value) => update("pcpCount", value as number)}
                    valueLabelDisplay="auto"
                    aria-label="Participating PCP count slider"
                    sx={{ color: TEAL, mt: 1 }}
                  />
                  {inputs.practiceType === "organization" && (
                    <Typography variant="caption" sx={{ color: MUTED }}>
                      Enter 101 or more above to see Custom Volume Enterprise guidance.
                    </Typography>
                  )}
                  <Accordion disableGutters elevation={0} sx={{ mt: 1.25, bgcolor: "transparent", "&::before": { display: "none" } }}>
                    <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ px: 0, minHeight: 36, "& .MuiAccordionSummary-content": { my: 0.5 } }}>
                      <Typography variant="caption" sx={{ color: TEAL_DARK, fontWeight: 800 }}>Who counts as a Participating PCP?</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 0, pt: 0 }}>
                      <Typography variant="caption" sx={{ color: MUTED, lineHeight: 1.6, display: "block" }}>
                        A Participating PCP is a PCP whose patients are eligible to receive MultiCog assessment under the group practice or organization’s subscription. Clinical and support personnel administering MultiCog on behalf of Participating PCPs do not require separate PCP licenses. Clinicians who merely view MultiCog results in the EHR do not require licenses.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Box>
              )}

              <Box sx={{ mt: 3.5 }}>
                {inputs.practiceType !== "individual" && (
                  <>
                    <Typography sx={{ fontWeight: 750, mb: 1 }}>How do you estimate AWV volume?</Typography>
                    <ToggleButtonGroup
                      exclusive
                      fullWidth
                      value={inputs.awvInputMode}
                      onChange={(_, value: AwvInputMode | null) => value && update("awvInputMode", value)}
                      aria-label="AWV volume input method"
                      sx={{ mb: 2, "& .MuiToggleButton-root": { textTransform: "none", py: 1.15, borderColor: BORDER, color: INK, fontWeight: 750 }, "& .Mui-selected": { bgcolor: "#e8f5f3 !important", color: `${TEAL_DARK} !important` } }}
                    >
                      <ToggleButton value="practice_total">Total annual AWVs</ToggleButton>
                      <ToggleButton value="per_pcp">Typical range per PCP</ToggleButton>
                    </ToggleButtonGroup>
                  </>
                )}
                <Typography sx={{ fontWeight: 750 }}>
                  {inputs.practiceType === "individual" || inputs.awvInputMode === "per_pcp"
                    ? "Typical annual AWV range per PCP"
                    : "Total annual AWV volume"}
                </Typography>
                <Typography variant="caption" sx={{ color: MUTED }}>
                  {inputs.practiceType === "individual"
                    ? "Choose the range for this individual PCP."
                    : inputs.awvInputMode === "per_pcp"
                      ? "Choose the typical range for one Participating PCP."
                      : "Use the combined total for the practice or organization."}
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  value={inputs.awvBand}
                  onChange={(_, value: AwvBand | null) => value && update("awvBand", value)}
                  aria-label="Annual AWV range"
                  sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1.5, "& .MuiToggleButtonGroup-grouped": { border: `1px solid ${BORDER} !important`, borderRadius: "999px !important", px: 2, py: 1, textTransform: "none", fontWeight: 700 }, "& .Mui-selected": { bgcolor: "#e8f5f3 !important", color: `${TEAL_DARK} !important`, borderColor: `${TEAL} !important` } }}
                >
                  {awvOptions.map((option) => <ToggleButton value={option.value} key={option.value}>{option.label}</ToggleButton>)}
                </ToggleButtonGroup>
              </Box>
            </Paper>

            {inputs.practiceType === "organization" && (
              <Paper elevation={0} sx={sectionSx}>
                <SectionHeader number="2" title="Select your IT and support level" description="Participating PCP licenses and service complexity are priced independently." />
                <Stack spacing={1.25}>
                  {supportOptions.map((option) => (
                    <Box
                      component="button"
                      type="button"
                      key={option.value}
                      onClick={() => update("supportLevel", option.value)}
                      aria-pressed={inputs.supportLevel === option.value}
                      sx={{
                        appearance: "none",
                        border: `1px solid ${inputs.supportLevel === option.value ? TEAL : BORDER}`,
                        bgcolor: inputs.supportLevel === option.value ? "#e8f5f3" : "white",
                        borderRadius: 2.5,
                        p: 2,
                        textAlign: "left",
                        cursor: "pointer",
                        color: INK,
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 2,
                        font: "inherit",
                      }}
                    >
                      <Box>
                        <Typography sx={{ fontWeight: 800 }}>{option.label}</Typography>
                        <Typography variant="body2" sx={{ color: MUTED, mt: 0.25 }}>{option.detail}</Typography>
                      </Box>
                      {inputs.supportLevel === option.value && <CheckRoundedIcon sx={{ color: TEAL, flex: "0 0 auto" }} />}
                    </Box>
                  ))}
                </Stack>
              </Paper>
            )}

            <Paper elevation={0} sx={sectionSx}>
              <SectionHeader number={inputs.practiceType === "organization" ? "3" : "2"} title="Tell us your EHR" description="This does not change the calculation; it helps identify integration requirements." />
              <FormControl fullWidth>
                <Select
                  displayEmpty
                  value={inputs.ehr}
                  onChange={(event) => update("ehr", event.target.value)}
                  inputProps={{ "aria-label": "Electronic health record system" }}
                >
                  <MenuItem value=""><em>Select an EHR (optional)</em></MenuItem>
                  {ehrOptions.map((ehr) => <MenuItem value={ehr} key={ehr}>{ehr}</MenuItem>)}
                </Select>
              </FormControl>
              {isStandardSelection && (
                <Box sx={{ mt: 2, p: 2, borderRadius: 2.5, bgcolor: "#f5f9f8", border: `1px solid ${BORDER}` }}>
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={inputs.includeStandardEhrIntegration}
                        onChange={(event) => update("includeStandardEhrIntegration", event.target.checked)}
                        sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: TEAL }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: TEAL } }}
                        slotProps={{ input: { "aria-label": "Add supported EHR integration" } }}
                      />
                    )}
                    label={(
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 800, color: INK }}>Add supported EHR integration</Typography>
                        <Typography variant="caption" sx={{ color: MUTED }}>+$39/month after compatibility confirmation</Typography>
                      </Box>
                    )}
                    sx={{ m: 0, alignItems: "center" }}
                  />
                </Box>
              )}
            </Paper>
          </Stack>

          <Paper
            component="aside"
            elevation={0}
            sx={{
              position: { md: "sticky" },
              top: { md: 24 },
              borderRadius: 3.5,
              overflow: "hidden",
              border: `1px solid ${BORDER}`,
              bgcolor: "white",
            }}
            aria-live="polite"
          >
            <Box sx={{ bgcolor: "#0b3740", color: "white", p: { xs: 2.5, sm: 3 } }}>
              <Typography variant="overline" sx={{ color: "#98ddd7", fontWeight: 800, letterSpacing: "0.11em" }}>
                {result.requiresQuote ? "Recommended path" : "Recommended plan"}
              </Typography>
              <Typography variant="h4" sx={{ mt: 0.75, fontWeight: 850, lineHeight: 1.1, letterSpacing: "-0.02em" }}>
                {result.planName}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, color: "rgba(255,255,255,0.72)" }}>{result.eyebrow}</Typography>

              {result.requiresQuote ? (
                <Box sx={{ mt: 3 }}>
                  <Typography sx={{ fontSize: 30, fontWeight: 850 }}>Custom quote</Typography>
                  <Typography variant="body2" sx={{ mt: 0.75, color: "rgba(255,255,255,0.72)" }}>A scoped quote is required for this configuration.</Typography>
                </Box>
              ) : (
                <Box sx={{ mt: 3 }}>
                  <Stack direction="row" spacing={0.8} sx={{ alignItems: "baseline" }}>
                    <Typography sx={{ fontSize: { xs: 44, sm: 52 }, lineHeight: 1, fontWeight: 900, letterSpacing: "-0.045em" }}>
                      {money.format(result.monthlyTotal ?? 0)}
                    </Typography>
                    <Typography sx={{ color: "rgba(255,255,255,0.68)" }}>/month</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap", mt: 2 }}>
                    <Chip label={`${money.format(result.annualTotal ?? 0)}/year`} size="small" sx={{ bgcolor: "rgba(255,255,255,0.12)", color: "white" }} />
                    <Chip label={`${moneyWithCents.format(result.perPcp ?? 0)}/PCP/month`} size="small" sx={{ bgcolor: "rgba(152,221,215,0.16)", color: "#b9efe9" }} />
                  </Stack>
                </Box>
              )}
            </Box>

            <Box sx={{ p: { xs: 2.5, sm: 3 } }}>
              <Typography variant="body2" sx={{ color: MUTED, lineHeight: 1.6 }}>{result.reason}</Typography>

              {!result.requiresQuote && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="overline" sx={{ color: MUTED, fontWeight: 800, letterSpacing: "0.09em" }}>Price breakdown</Typography>
                  <Stack divider={<Divider flexItem />} sx={{ mt: 0.5 }}>
                    {result.lineItems.map((item) => (
                      <Stack direction="row" sx={{ justifyContent: "space-between", gap: 2, py: 1.5 }} key={`${item.label}-${item.detail}`}>
                        <Box>
                          <Typography variant="body2" sx={{ color: INK, fontWeight: 750 }}>{item.label}</Typography>
                          <Typography variant="caption" sx={{ color: MUTED }}>{item.detail}</Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: INK, fontWeight: 850, whiteSpace: "nowrap" }}>{money.format(item.amount)}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              )}

              <Box sx={{ mt: 3, p: 2, borderRadius: 2.5, bgcolor: "#f5f9f8" }}>
                <Typography variant="overline" sx={{ color: MUTED, fontWeight: 800, letterSpacing: "0.09em" }}>Included</Typography>
                <Stack spacing={1.1} sx={{ mt: 0.75 }}>
                  {result.inclusions.map((item) => (
                    <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }} key={item}>
                      <CheckRoundedIcon sx={{ color: TEAL, fontSize: 19, mt: 0.15 }} />
                      <Typography variant="body2" sx={{ color: INK }}>{item}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>

              <Alert severity={result.isEstimate ? "warning" : "success"} sx={{ mt: 2.5 }}>
                {result.requiresQuote
                  ? "The calculator does not publish a price for this configuration. Cognimbus can prepare a volume quote from these inputs."
                  : result.isEstimate
                    ? "Estimated subscription based on the information provided. Final pricing can change if integration, security or implementation requirements differ materially."
                    : "Your subscription price for the selected configuration. Special integration or implementation work is excluded."}
              </Alert>

              <Accordion disableGutters elevation={0} sx={{ mt: 1.5, "&::before": { display: "none" }, borderTop: `1px solid ${BORDER}` }}>
                <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ px: 0 }}>
                  <Typography variant="body2" sx={{ color: TEAL_DARK, fontWeight: 850 }}>No hard utilization cap</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 0, pt: 0 }}>
                  <Typography variant="body2" sx={{ color: MUTED, lineHeight: 1.6 }}>
                    Keep assessing without interruption. Sustained utilization above your plan range may require moving to the appropriate plan at renewal.
                  </Typography>
                </AccordionDetails>
              </Accordion>

              {inputs.practiceType === "organization" && (
                <Accordion disableGutters elevation={0} sx={{ "&::before": { display: "none" }, borderTop: `1px solid ${BORDER}` }}>
                  <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ px: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>Participating PCP true-up</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 0, pt: 0 }}>
                    <Typography variant="body2" sx={{ color: MUTED, lineHeight: 1.6 }}>
                      Assessment remains uninterrupted. Participating PCP licenses may be reconciled periodically using attributed clinical use and aggregate utilization, with any adjustment handled at renewal or an agreed true-up interval.
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              )}

              <Accordion disableGutters elevation={0} sx={{ "&::before": { display: "none" }, borderTop: `1px solid ${BORDER}` }}>
                <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ px: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>EHR and calculation notes</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 0, pt: 0 }}>
                  <Typography variant="body2" sx={{ color: MUTED, lineHeight: 1.6 }}>{result.integrationNote}</Typography>
                  <Typography variant="body2" sx={{ color: MUTED, lineHeight: 1.6, mt: 1.25 }}>
                    Annual pricing is monthly pricing × 12. Taxes, custom integrations and one-time implementation services are not included.
                  </Typography>
                </AccordionDetails>
              </Accordion>

              <Button
                fullWidth
                variant="contained"
                endIcon={<OpenInNewRoundedIcon />}
                href="mailto:peterernster@icloud.com?subject=MultiCog%20plan%20calculator"
                sx={{ mt: 2.5, py: 1.4, bgcolor: TEAL, fontWeight: 800, textTransform: "none", borderRadius: 2, "&:hover": { bgcolor: TEAL_DARK } }}
              >
                {result.requiresQuote ? "Request a custom quote" : "Discuss this configuration"}
              </Button>
              <Button fullWidth variant="text" onClick={resetCalculator} sx={{ mt: 0.75, color: MUTED, fontWeight: 700, textTransform: "none" }}>
                Reset calculator
              </Button>
            </Box>
          </Paper>
            </Box>

            <Paper
              component="section"
              elevation={0}
              sx={{ mt: 4, p: { xs: 2.5, sm: 4 }, borderRadius: 3.5, border: `1px solid ${BORDER}`, bgcolor: "white" }}
              aria-labelledby="subscription-heading"
            >
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(360px, 0.8fr)" }, gap: { xs: 3, md: 5 }, alignItems: "center" }}>
                <Box>
                  <Typography variant="overline" sx={{ color: TEAL_DARK, fontWeight: 900, letterSpacing: "0.1em" }}>
                    Next step
                  </Typography>
                  <Typography id="subscription-heading" component="h2" sx={{ color: INK, fontSize: { xs: 28, sm: 34 }, fontWeight: 900, letterSpacing: "-0.025em", mt: 0.4 }}>
                    Ready when you are.
                  </Typography>
                  <Typography sx={{ color: MUTED, mt: 1.1, lineHeight: 1.65, maxWidth: 610 }}>
                    Choose whether you want to subscribe now or save this configuration for later. Secure payment checkout will be connected to Subscribe now in a future release.
                  </Typography>
                </Box>

                <Box>
                  <TextField
                    label="Your email address"
                    type="email"
                    value={subscriptionEmail}
                    onChange={(event) => {
                      setSubscriptionEmail(event.target.value);
                      if (subscriptionEmailTouched) setSubscriptionEmailTouched(true);
                    }}
                    error={subscriptionEmailTouched && !isSubscriptionEmailValid}
                    helperText={subscriptionEmailTouched && !isSubscriptionEmailValid
                      ? "Enter a valid email address."
                      : "Used only for this subscription request."}
                    autoComplete="email"
                    required
                    fullWidth
                    slotProps={{ htmlInput: { "aria-label": "Your email address" } }}
                  />
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} sx={{ mt: 1.5 }}>
                    <Button
                      href={subscriptionMailto("now")}
                      onClick={handleSubscriptionClick}
                      variant="contained"
                      startIcon={<CreditCardRoundedIcon />}
                      sx={{ flex: 1, py: 1.3, bgcolor: TEAL, textTransform: "none", fontWeight: 850, borderRadius: 2, "&:hover": { bgcolor: TEAL_DARK } }}
                    >
                      Subscribe now
                    </Button>
                    <Button
                      href={subscriptionMailto("later")}
                      onClick={handleSubscriptionClick}
                      variant="outlined"
                      startIcon={<ScheduleRoundedIcon />}
                      sx={{ flex: 1, py: 1.3, borderColor: TEAL, color: TEAL_DARK, textTransform: "none", fontWeight: 850, borderRadius: 2 }}
                    >
                      Subscribe later
                    </Button>
                  </Stack>
                  <Typography variant="caption" sx={{ color: MUTED, display: "block", mt: 1.25, lineHeight: 1.5 }}>
                    For now, both buttons prepare an email with your selected configuration. No payment is taken on this page.
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </>
        ) : (
          <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3, border: `1px dashed ${BORDER}`, bgcolor: "rgba(255,255,255,0.72)", textAlign: "center" }}>
            <LockRoundedIcon sx={{ color: "#789098", fontSize: 36 }} />
            <Typography sx={{ color: INK, fontWeight: 850, mt: 1 }}>Calculator access is locked</Typography>
            <Typography variant="body2" sx={{ color: MUTED, mt: 0.5 }}>
              Complete professional verification above to access plan pricing.
            </Typography>
          </Paper>
        )}
      </Container>

      <Box component="footer" sx={{ borderTop: `1px solid ${BORDER}`, bgcolor: "white", py: 4 }}>
        <Container maxWidth="lg">
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ justifyContent: "space-between", alignItems: { sm: "center" } }}>
            <Box>
              <Typography sx={{ fontWeight: 800, color: INK }}>MultiCog by SaccaScan.ai</Typography>
              <Typography variant="body2" sx={{ color: MUTED, mt: 0.25 }}>Cognitive assessment designed for primary care workflows.</Typography>
            </Box>
            <Typography variant="caption" sx={{ color: MUTED, maxWidth: 490 }}>
              This calculator estimates subscription costs only. It does not provide reimbursement, coding, clinical or legal advice and does not guarantee coverage or payment.
            </Typography>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}

export default CalculatorPage;
